import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  CheckCircle2,
  ArrowRight,
  Home,
  Clock,
  Mail,
  ClipboardCheck,
  Truck,
  Phone,
} from "lucide-react";

const TITLE = "Application Received | Free Vending Machines USA";
const DESC =
  "Thanks for applying for a FREE vending machine. Our placement team aims to respond in 4–6 hours during US business hours.";

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "noindex, nofollow" },
      { name: "googlebot", content: "noindex, nofollow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://vendingmachinefree.com/thank-you" }],
  }),
  component: ThankYou,
});

function ThankYou() {
  useEffect(() => {
    // Conversion analytics — fires on confirmation page load
    try {
      const w = window as unknown as {
        dataLayer?: Array<Record<string, unknown>>;
        gtag?: (...args: unknown[]) => void;
        plausible?: (event: string, opts?: Record<string, unknown>) => void;
      };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: "form_submission_success",
        form_id: "vending_application",
        page_path: "/thank-you",
      });
      w.gtag?.("event", "conversion", {
        event_category: "lead",
        event_label: "vending_application",
        send_to: "form_submission",
      });
      w.plausible?.("Form Submission", { props: { form: "vending_application" } });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  return (
    <main className="min-h-screen bg-secondary px-4 py-12 md:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="bg-card border border-border rounded-xl p-8 md:p-12 text-center shadow-[var(--shadow-industrial)]">
          <div className="mx-auto h-16 w-16 rounded-full bg-safety/10 flex items-center justify-center">
            <CheckCircle2 className="h-9 w-9 text-safety" />
          </div>
          <h1 className="mt-6 font-display text-3xl md:text-4xl uppercase">
            Application Received
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            Thanks for reaching out — your request for a FREE vending machine is in.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-steel/10 px-4 py-2 text-sm font-semibold text-steel">
            <Clock className="h-4 w-4" />
            We endeavour to be the fastest in America–USA
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            We try to respond within <span className="font-semibold text-foreground">4–6 hours</span>{" "}
            during US business hours — times do vary depending on our team's current load, but a
            real human will be in touch as soon as possible.
          </p>
        </div>

        <section className="mt-8 bg-card border border-border rounded-xl p-6 md:p-8">
          <h2 className="font-display text-xl md:text-2xl uppercase">What happens next</h2>
          <ol className="mt-5 space-y-4">
            {[
              {
                Icon: Mail,
                title: "Confirmation email sent",
                body: "A copy of your enquiry has been sent to the email you provided. If it doesn't arrive within a few minutes, check your spam/junk folder.",
              },
              {
                Icon: ClipboardCheck,
                title: "Qualification review (4–6 hrs)",
                body: "Our placement team reviews your location, foot traffic and machine type to match the right equipment.",
              },
              {
                Icon: Phone,
                title: "Quick call or reply",
                body: "We'll reach out to confirm details and schedule a free on-site survey — no obligation.",
              },
              {
                Icon: Truck,
                title: "Free install & service",
                body: "Approved sites get delivery, install, restocking and maintenance at no cost — 100% free.",
              },
            ].map(({ Icon, title, body }, i) => (
              <li key={title} className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-steel text-steel-foreground font-bold">
                  {i + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    <Icon className="h-4 w-4 text-safety" /> {title}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition"
          >
            <Home className="h-4 w-4" /> Back to Home
          </Link>
          <Link
            to="/vending-machines"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-5 py-3 font-semibold hover:bg-accent transition"
          >
            Browse Machines <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/coverage"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-5 py-3 font-semibold hover:bg-accent transition"
          >
            Nationwide Coverage <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Ref: a confirmation email has been sent to the address you provided.
        </p>
      </div>
    </main>
  );
}
